import { useEffect, useMemo, useState, useCallback } from "react";
import api, { extractError } from "@/lib/api";
import { SectionHeader } from "@/components/SectionHeader";
import { StatusPill } from "@/components/StatusPill";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Plus, Search, MoreHorizontal, KeyRound, ToggleLeft, ToggleRight, Trash2, PencilLine, ShieldCheck } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { ROLE_LABEL, formatDate, initials } from "@/lib/format";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";

const ROLE_OPTIONS = [
  { value: "manager", label: "Manager" },
  { value: "team_leader", label: "Team Leader" },
  { value: "sales_executive", label: "Sales Executive" },
];

const empty = {
  name: "", employee_id: "", email: "", mobile: "", role: "sales_executive",
  manager_id: "", team_leader_id: "", joining_date: "", password: "",
  can_approve_submissions: false,
};

function reportsToLabel({ tl, mgr }) {
  if (tl) return `TL: ${tl.name}`;
  if (mgr) return `Mgr: ${mgr.name}`;
  return "—";
}

function generatePassword() {
  // 12-char password with upper/lower/digit/symbol
  const upper = "ABCDEFGHJKLMNPQRSTUVWXYZ";
  const lower = "abcdefghijkmnpqrstuvwxyz";
  const digits = "23456789";
  const symbols = "!@#$%&*";
  const all = upper + lower + digits + symbols;
  const arr = new Uint32Array(12);
  window.crypto.getRandomValues(arr);
  const pick = (pool, idx) => pool[arr[idx] % pool.length];
  const req = [pick(upper, 0), pick(lower, 1), pick(digits, 2), pick(symbols, 3)];
  const rest = Array.from({ length: 8 }, (_, i) => pick(all, i + 4));
  return [...req, ...rest].sort(() => (window.crypto.getRandomValues(new Uint8Array(1))[0] % 2) - 0.5).join("");
}

export default function UsersPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [pwdOpen, setPwdOpen] = useState(null);
  const [newPwd, setNewPwd] = useState("");

  const load = useCallback(async () => {
    const params = {};
    if (q) params.q = q;
    if (roleFilter !== "all") params.role = roleFilter;
    const { data } = await api.get("/users", { params });
    setUsers(data);
  }, [q, roleFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const managers = useMemo(() => users.filter((u) => u.role === "manager"), [users]);
  const leaders = useMemo(() => users.filter((u) => u.role === "team_leader"), [users]);

  const startEdit = (u) => {
    setEditing(u);
    setForm({
      name: u.name, employee_id: u.employee_id, email: u.email, mobile: u.mobile,
      role: u.role, manager_id: u.manager_id || "", team_leader_id: u.team_leader_id || "",
      joining_date: u.joining_date || "", password: "",
      can_approve_submissions: !!u.can_approve_submissions,
    });
    setOpen(true);
  };

  const startCreate = () => {
    setEditing(null);
    setForm(empty);
    setOpen(true);
  };

  const submit = async () => {
    try {
      const payload = { ...form };
      if (!payload.manager_id) payload.manager_id = null;
      if (!payload.team_leader_id) payload.team_leader_id = null;
      // Approval flag is manager-only; strip it for other roles so the
      // backend doesn't have to guess our intent.
      if (payload.role !== "manager") {
        payload.can_approve_submissions = false;
      }
      if (editing) {
        const { password, ...rest } = payload;
        await api.patch(`/users/${editing.id}`, rest);
        toast.success("User updated");
      } else {
        if (!payload.password || payload.password.length < 6) {
          toast.error("Password is required (min 6 characters). Use 'Generate' to auto-create.");
          return;
        }
        await api.post("/users", payload);
        toast.success("User created");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(extractError(e));
    }
  };

  const toggle = async (u) => {
    try {
      await api.post(`/users/${u.id}/toggle-active`);
      toast.success(`User ${u.is_active ? "deactivated" : "activated"}`);
      load();
    } catch (e) {
      toast.error(extractError(e));
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.name}?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("User deleted");
      load();
    } catch (e) {
      toast.error(extractError(e));
    }
  };

  const resetPwd = async () => {
    if (!pwdOpen || newPwd.length < 6) return toast.error("Password must be 6+ chars");
    try {
      await api.post(`/users/${pwdOpen.id}/reset-password`, { new_password: newPwd });
      toast.success("Password reset");
      setPwdOpen(null);
      setNewPwd("");
    } catch (e) {
      toast.error(extractError(e));
    }
  };

  const filtered = useMemo(() => {
    if (!q.trim()) return users;
    const s = q.toLowerCase();
    return users.filter(
      (u) => u.name.toLowerCase().includes(s) ||
             u.employee_id.toLowerCase().includes(s) ||
             u.email.toLowerCase().includes(s)
    );
  }, [users, q]);

  const canManage = user.role === "admin";

  return (
    <div data-testid="users-page">
      <SectionHeader
        title="Users & Hierarchy"
        description="Manage employees, assign managers & team leaders, and control access."
        actions={
          canManage && (
            <Button onClick={startCreate} data-testid="create-user-btn">
              <Plus className="mr-2 h-4 w-4" /> New User
            </Button>
          )
        }
      />

      <Card className="rounded-xl border border-border bg-card p-4 shadow-sm">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="relative w-full sm:max-w-xs">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              data-testid="users-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, employee ID, email…"
              className="pl-9"
            />
          </div>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger data-testid="users-role-filter" className="w-full sm:w-56">
              <SelectValue placeholder="All roles" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All roles</SelectItem>
              <SelectItem value="admin">Admin</SelectItem>
              <SelectItem value="manager">Manager</SelectItem>
              <SelectItem value="team_leader">Team Leader</SelectItem>
              <SelectItem value="sales_executive">Sales Executive</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="overflow-x-auto rounded-md border border-border">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/40">
                <TableHead className="label-caps">Employee</TableHead>
                <TableHead className="label-caps">Role</TableHead>
                <TableHead className="label-caps">Reports to</TableHead>
                <TableHead className="label-caps">Contact</TableHead>
                <TableHead className="label-caps">Status</TableHead>
                <TableHead className="label-caps">Joined</TableHead>
                {canManage && <TableHead className="w-12" />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((u) => {
                const mgr = users.find((x) => x.id === u.manager_id);
                const tl = users.find((x) => x.id === u.team_leader_id);
                return (
                  <TableRow key={u.id} data-testid={`user-row-${u.employee_id}`} className="hover:bg-muted/40">
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar className="h-8 w-8">
                          <AvatarFallback className="bg-primary/10 text-xs font-semibold text-primary">{initials(u.name)}</AvatarFallback>
                        </Avatar>
                        <div>
                          <div className="font-medium">{u.name}</div>
                          <div className="text-xs text-muted-foreground tabular-nums">{u.employee_id}</div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <StatusPill status={u.role === "admin" ? "active" : "new"}>{ROLE_LABEL[u.role]}</StatusPill>
                        {u.role === "manager" && u.can_approve_submissions && (
                          <span
                            data-testid={`user-approver-badge-${u.employee_id}`}
                            title="Can approve submissions"
                            className="inline-flex items-center gap-1 rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-primary"
                          >
                            <ShieldCheck className="h-3 w-3" /> Approver
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {reportsToLabel({ tl, mgr })}
                    </TableCell>
                    <TableCell className="text-sm">
                      <div>{u.email}</div>
                      <div className="text-xs text-muted-foreground tabular-nums">{u.mobile}</div>
                    </TableCell>
                    <TableCell>
                      <StatusPill status={u.is_active ? "active" : "inactive"}>
                        {u.is_active ? "Active" : "Inactive"}
                      </StatusPill>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground tabular-nums">{formatDate(u.joining_date)}</TableCell>
                    {canManage && (
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`user-menu-${u.employee_id}`}>
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => startEdit(u)}>
                              <PencilLine className="mr-2 h-4 w-4" /> Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setPwdOpen(u)}>
                              <KeyRound className="mr-2 h-4 w-4" /> Reset Password
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => toggle(u)}>
                              {u.is_active
                                ? (<><ToggleLeft className="mr-2 h-4 w-4" /> Deactivate</>)
                                : (<><ToggleRight className="mr-2 h-4 w-4" /> Activate</>)}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="text-[hsl(var(--danger))] focus:text-[hsl(var(--danger))]"
                              onClick={() => remove(u)}
                            >
                              <Trash2 className="mr-2 h-4 w-4" /> Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="py-10 text-center text-sm text-muted-foreground">No users found.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Create/Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="user-form-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit user" : "Create user"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Full name</Label>
              <Input data-testid="user-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Employee ID</Label>
              <Input data-testid="user-form-empid" value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input data-testid="user-form-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Mobile</Label>
              <Input data-testid="user-form-mobile" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-form-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLE_OPTIONS.map((r) => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Joining date</Label>
              <Input type="date" value={form.joining_date} onChange={(e) => setForm({ ...form, joining_date: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Manager</Label>
              <Select value={form.manager_id || "none"} onValueChange={(v) => setForm({ ...form, manager_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="user-form-manager"><SelectValue placeholder="Select manager" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— None —</SelectItem>
                  {managers.map((m) => (<SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Team Leader</Label>
              <Select value={form.team_leader_id || "none"} onValueChange={(v) => setForm({ ...form, team_leader_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="user-form-tl"><SelectValue placeholder="Select TL" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— None —</SelectItem>
                  {leaders.map((m) => (<SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            {!editing && (
              <div className="space-y-1.5 sm:col-span-2">
                <Label>Initial password</Label>
                <div className="flex gap-2">
                  <Input
                    data-testid="user-form-password"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    placeholder="Enter or generate a password (min 6 chars)"
                  />
                  <Button
                    type="button"
                    variant="secondary"
                    data-testid="user-form-generate-password"
                    onClick={() => setForm({ ...form, password: generatePassword() })}
                  >
                    Generate
                  </Button>
                </div>
                <p className="text-[11px] text-muted-foreground">
                  Share the password securely with the employee — they can change it after first login.
                </p>
              </div>
            )}

            {form.role === "manager" && (
              <div
                data-testid="user-form-permissions"
                className="sm:col-span-2 flex items-start justify-between gap-4 rounded-lg border border-border bg-muted/30 p-4"
              >
                <div className="space-y-1">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <ShieldCheck className="h-4 w-4 text-primary" />
                    Can Approve Submissions
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Grants this manager the right to approve or reject submissions from users
                    within their reporting hierarchy. Managers can never act on submissions
                    outside their downline.
                  </p>
                </div>
                <Switch
                  data-testid="user-form-can-approve"
                  checked={!!form.can_approve_submissions}
                  onCheckedChange={(v) => setForm({ ...form, can_approve_submissions: !!v })}
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} data-testid="user-form-submit">{editing ? "Save changes" : "Create user"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset password dialog */}
      <Dialog open={!!pwdOpen} onOpenChange={(o) => !o && setPwdOpen(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reset password — {pwdOpen?.name}</DialogTitle></DialogHeader>
          <div className="space-y-1.5">
            <Label>New password</Label>
            <Input data-testid="reset-pwd-input" type="text" value={newPwd} onChange={(e) => setNewPwd(e.target.value)} placeholder="At least 6 characters" />
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setPwdOpen(null)}>Cancel</Button>
            <Button onClick={resetPwd} data-testid="reset-pwd-submit">Reset password</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
